/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSetter } from '@cloudio-saas/statex';
import { Alert, AlertTitle, Box, Link } from '@mui/material';
import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  Editor,
  Element as SlateElement,
  Node,
  Text as SlateText,
  Transforms,
} from 'slate';
import { jsx } from 'slate-hyperscript';
import {
  RenderElementProps,
  RenderLeafProps,
  useFocused,
  useSelected,
  useSlateStatic,
} from 'slate-react';
import { useTheme } from 'theme/Theme';
import { isEmpty, isNull } from 'utils/isEmpty';
import useJoinRef from 'utils/useJoinRef';
import {
  El,
  ElementTags,
  ELEMENT_TAGS,
  MentionElement,
  TextTags,
  TEXT_TAGS,
} from './SlateTypes';
import { floatingActionPositionAtom, useSlateContext } from './SlateUtils';

const EMPTY_CHILD = Object.freeze({ text: '' }) as El;

export const deserialize = (el: HTMLElement | ChildNode): El | El[] => {
  if (el.nodeType === 3) {
    return el.textContent;
  }
  if (el.nodeType !== 1) {
    return null;
  }
  if (el.nodeName === 'BR') {
    return '\n';
  }

  const { nodeName } = el;
  let parent = el;

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0].nodeName === 'CODE'
  ) {
    parent = el.childNodes[0] as HTMLElement;
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat();

  if (children.length === 0) {
    children = [EMPTY_CHILD];
  }

  if (el.nodeName === 'BODY') {
    children = children.filter((c) => c !== EMPTY_CHILD);
    return jsx('fragment', {}, children);
  }

  if (ELEMENT_TAGS[nodeName as ElementTags]) {
    const attrs = ELEMENT_TAGS[nodeName as ElementTags](el as HTMLElement);
    return jsx('element', attrs, children);
  }

  if (TEXT_TAGS[nodeName as TextTags]) {
    const attrs = TEXT_TAGS[nodeName as TextTags]();
    return children.map((child) => jsx('text', attrs, child));
  }

  return children;
};

export const withHtml = (editor: Editor): Editor => {
  const { insertData, isInline, isVoid } = editor;

  editor.isInline = (element: SlateElement) => {
    return element.type === 'a' ? true : isInline(element);
  };

  editor.isVoid = (element: SlateElement) => {
    return element.type === 'img' || element.type === 'video'
      ? true
      : isVoid(element);
  };

  editor.insertData = (data) => {
    const html = data.getData('text/html');
    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      let fragment = deserialize(parsed.body) as Node[];
      fragment = fragment.map((e) => {
        if (SlateText.isText(e)) {
          return { type: 'p', children: [e] };
        }
        return e;
      });
      Transforms.insertFragment(editor, fragment);
      return;
    }
    insertData(data);
  };

  return editor;
};

export const SlateHTMLElement = (props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const selected = useSelected();
  const ref = useRef<HTMLElement>(null);
  const set = useStateXSetter();
  const ctx = useSlateContext();
  const e = useSlateStatic();

  useLayoutEffect(() => {
    if (ref.current && selected) {
      const top = ref.current.offsetTop;

      if (ctx.lastSelection) {
        const parent = Editor.parent(e, ctx.lastSelection, {
          edge: 'end',
        });
        if (parent[0] === element) {
          const emptyLine =
            element.type === 'p' &&
            element.children.length === 1 &&
            element.children[0].text === '';
          set(floatingActionPositionAtom, (pos) => ({
            ...pos,
            top,
            type: element.type,
            emptyLine,
            linkPopoverVisible: false,
          }));
        }
      }
    }
  }, [ctx.editor, ctx.lastSelection, e, element, element.type, selected, set]);

  let content;

  const joinRef = useJoinRef(attributes.ref, ref);

  switch (element.type) {
    case 'quote':
      content = (
        <blockquote {...attributes} ref={joinRef}>
          {children}
        </blockquote>
      );
      break;
    case 'code':
      content = (
        <pre>
          <code {...attributes} ref={joinRef}>
            {children}
          </code>
        </pre>
      );
      break;
    case 'ul':
      content = (
        <ul {...attributes} ref={joinRef}>
          {children}
        </ul>
      );
      break;
    case 'h1':
      content = (
        <h1 {...attributes} ref={joinRef}>
          {children}
        </h1>
      );
      break;
    case 'h2':
      content = (
        <h2 {...attributes} ref={joinRef}>
          {children}
        </h2>
      );
      break;
    case 'h3':
      content = (
        <h3 {...attributes} ref={joinRef}>
          {children}
        </h3>
      );
      break;
    case 'h4':
      content = (
        <h4 {...attributes} ref={joinRef}>
          {children}
        </h4>
      );
      break;
    case 'h5':
      return (
        <h5 {...attributes} ref={joinRef}>
          {children}
        </h5>
      );
    case 'h6':
      content = (
        <h6 {...attributes} ref={joinRef}>
          {children}
        </h6>
      );
      break;
    case 'li':
      content = (
        <li {...attributes} ref={joinRef}>
          {children}
        </li>
      );
      break;
    case 'ol':
      content = (
        <ol {...attributes} ref={joinRef}>
          {children}
        </ol>
      );
      break;
    case 'a':
      content = (
        <Link
          href={element.url}
          {...attributes}
          target="_blank"
          rel="noreferrer"
          ref={joinRef}
          contentEditable={false}
        >
          {children}
        </Link>
      );
      break;
    case 'img':
      content = (
        <ImageElement {...props} attributes={{ ...attributes, ref: joinRef }} />
      );
      break;
    case 'video':
      content = (
        <VideoElement {...props} attributes={{ ...attributes, ref: joinRef }} />
      );
      break;
    case 'mention':
      content = (
        <MentionComponent
          {...props}
          attributes={{ ...attributes, ref: joinRef }}
        />
      );
      break;
    case 'info':
    case 'success':
    case 'warning':
    case 'error':
      // TODO: Need to add a way to change the title dynamically
      let title = 'Info';
      switch (element.type) {
        case 'success':
          title = 'Success';
          break;
        case 'warning':
          title = 'Warning';
          break;
        case 'error':
          title = 'Error';
          break;
        default:
          break;
      }
      content = (
        <Alert
          {...attributes}
          severity={element.type}
          variant="standard"
          ref={joinRef}
        >
          <AlertTitle contentEditable={false}>{title}</AlertTitle>
          {children}
        </Alert>
      );
      break;
    default:
      content = (
        <p {...attributes} ref={joinRef}>
          {children}
        </p>
      );
  }
  return content;
};

const regex = /\[\[([^\]]+)\]\]/gm;

export function getSpecialKeys(
  text: string,
): IterableIterator<RegExpMatchArray> {
  return text?.matchAll(regex) ?? [];
}

export function TextWithKeys({ text }: { text: string }) {
  const comps = useMemo(() => {
    if (isEmpty(text)) return;
    const res = getSpecialKeys(text);
    let startIndex = 0;
    const _comps = [];
    for (const match of res) {
      const [fullMatch, matchText] = match;
      if (!isNull(match.index)) {
        const val = text.substring(startIndex, match.index);
        startIndex = match.index + fullMatch.length;
        if (val.length > 0) {
          _comps.push(val);
        }
        if (matchText === ' ') {
          _comps.push(' ');
        } else {
          _comps.push(
            <code
              style={{
                backgroundColor: '#eee',
                padding: '0 8px',
                margin: '0 4px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                lineHeight: '1.4rem',
              }}
              key={`${matchText}_${startIndex}`}
            >
              {matchText}
            </code>,
          );
        }
      }
    }
    if (startIndex === 0) {
      return text;
    }
    if (text.length > startIndex) {
      _comps.push(text.substring(startIndex));
    }
    return _comps;
  }, [text]);

  return <>{comps}</>;
}

// function ParaWithKeys(props: any) {
//   const { children, attributes, element } = props;
//   const [childs, setChilds] = useState<(string | JSX.Element)[]>([]);
//   const [text, setText] = useState('');

//   useEffect(() => {
//     let txt = '';
//     if (
//       element.children.length === 1 &&
//       element.children[0].text?.indexOf('[[') >= 0 &&
//       element.children[0].text?.indexOf(']]') >= 0
//     ) {
//       txt = element.children[0].text;
//       setText(txt);
//     } else {
//       setChilds(children);
//     }
//   }, [children, element.children, element.children.length]);

//   return text ? <TextWithKeys text={text} /> : <p {...attributes}>{childs}</p>;
// }

const MentionComponent = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const selected = useSelected();
  const focused = useFocused();
  const theme = useTheme();
  const ele = element as MentionElement;

  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        padding: '0 4px',
        margin: '0 1px',
        verticalAlign: 'baseline',
        display: 'inline-block',
        borderRadius: '4px',
        backgroundColor: theme.palette.action.selected,
        fontSize: '0.9em',
        boxShadow:
          selected && focused
            ? `0 0 0 2px ${theme.palette.primary.main}`
            : 'none',
      }}
    >
      {ele.option.label || ele.option.value}
      {children}
    </span>
  );
};

const ImageElement = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const selected = useSelected();
  const focused = useFocused();
  const theme = useTheme();
  return (
    <div {...attributes}>
      {children}
      <Box
        component="img"
        src={element.url}
        sx={{
          display: 'block',
          maxWidth: '100%',
          boxShadow:
            selected && focused
              ? `0 0 0 2px ${theme.palette.primary.main};`
              : 'none',
        }}
      />
    </div>
  );
};

const VideoElement = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const { url } = element;
  const selected = useSelected();
  const theme = useTheme();
  return (
    <div {...attributes}>
      <div contentEditable={false}>
        <Box
          sx={{
            position: 'relative',
            padding: '75% 0 0 0',
          }}
        >
          <Box
            sx={{
              zIndex: 1,
              flex: 1,
              position: 'absolute',
              left: -40,
              width: 40,
              top: 0,
              bottom: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '2px',
              left: '2px',
              right: '2px',
              bottom: '2px',
              boxShadow: selected
                ? `0 0 0 2px ${theme.palette.primary.main}`
                : 'none',
            }}
          >
            {url?.startsWith('https://') ? (
              <iframe
                title="video"
                src={`${url}?title=0&byline=0&portrait=0`}
                frameBorder="0"
                allowFullScreen
                allow="autoplay; fullscreen; picture-in-picture"
                style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '100%',
                  height: '100%',
                }}
              />
            ) : (
              <>{url}</>
            )}
          </Box>
        </Box>
      </div>
      {children}
    </div>
  );
};

export const SlateLeaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.strikethrough) {
    children = <del>{children}</del>;
  }

  if (leaf.color) {
    children = <span style={{ color: leaf.color }}>{children}</span>;
  }

  if (leaf.backgroundColor) {
    children = (
      <span style={{ backgroundColor: leaf.backgroundColor }}>{children}</span>
    );
  }

  return <span {...attributes}>{children}</span>;
};
